import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";

import { api } from "../../api/api";
import { processingApi } from "../../api/processingApi";
import {
  ProcessingLine,
  ProcessingLinePayload,
  ProcessingItemType,
} from "../../types/processing.type";

const { Title, Text } = Typography;

interface StockOption {
  value: string;
  label: string;
  itemType: ProcessingItemType;
  unitOfMeasurement: string;
}

/** Ключ позиции: id одного мало, продукция и материал могут совпасть по id. */
const optionKey = (itemType: ProcessingItemType, itemId: string) => `${itemType}:${itemId}`;

export default function ProcessingLines() {
  const [lines, setLines] = useState<ProcessingLine[]>([]);
  const [lossLimitPercent, setLossLimitPercent] = useState<number>(2);
  const [lossLimitEnabled, setLossLimitEnabled] = useState<boolean>(true);
  const [outputLimitEnabled, setOutputLimitEnabled] = useState<boolean>(true);

  const [options, setOptions] = useState<StockOption[]>([]);
  const [machines, setMachines] = useState<Array<{ _id: string; name: string }>>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<ProcessingLine | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [form] = Form.useForm();

  const fetchLines = async () => {
    setLoading(true);
    try {
      const data = await processingApi.getLines();
      setLines(data.lines || []);
      setLossLimitPercent(data.lossLimitPercent ?? 2);
      setLossLimitEnabled(data.lossLimitEnabled !== false);
      setOutputLimitEnabled(data.outputLimitEnabled !== false);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось загрузить линии переработки.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      // showHidden=true обязателен: позиция потери заводится скрытой из продаж,
      // иначе её не выбрать в конфигурации.
      const [producesRes, materialsRes, machinesRes] = await Promise.all([
        api.get("/produce/list", { params: { showHidden: "true" } }),
        api.get("/material/list"),
        api.get("/machine/list"),
      ]);

      const produces: StockOption[] = (producesRes.data || []).map((p: any) => ({
        value: optionKey("Produce", p._id),
        label: `${p.name} — продукция`,
        itemType: "Produce" as ProcessingItemType,
        unitOfMeasurement: p.unitOfMeasurement,
      }));

      const materials: StockOption[] = (materialsRes.data || []).map((m: any) => ({
        value: optionKey("Material", m._id),
        label: `${m.name} — материал`,
        itemType: "Material" as ProcessingItemType,
        unitOfMeasurement: m.unitOfMeasurement,
      }));

      setOptions([...produces, ...materials]);
      setMachines(machinesRes.data?.machines || machinesRes.data || []);
    } catch {
      message.error("Не удалось загрузить справочник позиций.");
    }
  };

  useEffect(() => {
    fetchLines();
    fetchOptions();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormError(null);
    form.resetFields();
    form.setFieldsValue({
      costAllocation: "salesValue",
      isActive: false,
      outputs: [{ normPercent: 0, lowerLimitPercent: 5, upperLimitPercent: 5, isRequired: true }],
    });
    setModalOpen(true);
  };

  const openEdit = (line: ProcessingLine) => {
    setEditing(line);
    setFormError(null);
    form.setFieldsValue({
      name: line.name,
      machineId: line.machineId || null,
      input: optionKey(line.input.itemType, line.input.itemId),
      loss: optionKey(line.loss.itemType, line.loss.itemId),
      costAllocation: line.costAllocation,
      isActive: line.isActive,
      outputs: line.outputs.map((output) => ({
        item: optionKey(output.itemType, output.itemId),
        normPercent: output.normPercent,
        lowerLimitPercent: output.lowerLimitPercent,
        upperLimitPercent: output.upperLimitPercent,
        isRequired: output.isRequired,
      })),
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    setFormError(null);

    try {
      const split = (key: string) => {
        const [itemType, itemId] = String(key).split(":");
        return { itemType: itemType as ProcessingItemType, itemId };
      };

      const payload: ProcessingLinePayload = {
        name: values.name,
        machineId: values.machineId || null,
        input: split(values.input),
        loss: split(values.loss),
        costAllocation: values.costAllocation,
        isActive: Boolean(values.isActive),
        outputs: (values.outputs || []).map((output: any) => ({
          ...split(output.item),
          normPercent: output.normPercent,
          lowerLimitPercent: output.lowerLimitPercent,
          upperLimitPercent: output.upperLimitPercent,
          isRequired: output.isRequired !== false,
        })),
      };

      if (editing) {
        await processingApi.updateLine(editing._id, payload);
        message.success("Линия обновлена.");
      } else {
        await processingApi.createLine(payload);
        message.success("Линия создана.");
      }

      setModalOpen(false);
      fetchLines();
    } catch (error: any) {
      // Сервер перечисляет незаполненные цены списком — показываем целиком,
      // иначе администратор не поймёт, какие позиции чинить.
      setFormError(error?.response?.data?.error || "Не удалось сохранить линию.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (line: ProcessingLine) => {
    try {
      await processingApi.deleteLine(line._id);
      message.success("Линия удалена.");
      fetchLines();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось удалить линию.", 8);
    }
  };

  const columns = useMemo(
    () => [
      { title: "Название", dataIndex: "name", key: "name" },
      {
        title: "Вход",
        key: "input",
        render: (_: any, line: ProcessingLine) => (
          <Text>
            {line.input.itemName}{" "}
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({line.input.unitOfMeasurement})
            </Text>
          </Text>
        ),
      },
      {
        title: "Выходы",
        key: "outputs",
        render: (_: any, line: ProcessingLine) => (
          <Space direction="vertical" size={2}>
            {line.outputs.map((output) => (
              <Text key={output.itemId} style={{ fontSize: 13 }}>
                {output.itemName} — норма {output.normPercent} % (−{output.lowerLimitPercent} / +
                {output.upperLimitPercent} %)
              </Text>
            ))}
          </Space>
        ),
      },
      {
        title: "Потеря",
        key: "loss",
        render: (_: any, line: ProcessingLine) => <Text>{line.loss.itemName}</Text>,
      },
      {
        title: "Разноска",
        key: "costAllocation",
        render: (_: any, line: ProcessingLine) =>
          line.costAllocation === "salesValue" ? "По стоимости продажи" : "По массе",
      },
      {
        title: "Статус",
        key: "isActive",
        render: (_: any, line: ProcessingLine) =>
          line.isActive ? <Tag color="success">Включена</Tag> : <Tag>Выключена</Tag>,
      },
      {
        title: "",
        key: "actions",
        render: (_: any, line: ProcessingLine) => (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => openEdit(line)} type="link">
              Изменить
            </Button>
            <Popconfirm
              title="Удалить линию?"
              description="Удалить можно только линию без проведённых загрузок."
              okText="Удалить"
              cancelText="Отмена"
              onConfirm={() => handleDelete(line)}
            >
              <Button icon={<DeleteOutlined />} type="link" danger>
                Удалить
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Линии переработки
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Добавить линию
        </Button>
      </Space>

      {/* Потолок потери живёт в общих настройках — здесь только показываем, */}
      {/* чтобы администратор не искал его на второй странице.              */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <span>
            Потолок потери: <b>{lossLimitPercent} %</b>{" "}
            {lossLimitEnabled ? "(блокирует)" : "(тумблер выключен)"} · коридоры выходов{" "}
            {outputLimitEnabled ? "блокируют" : "не блокируют"}
          </span>
        }
        description="Потолок потери и оба тумблера задаются в общих настройках, раздел «Переработка». Здесь настраиваются нормы и коридоры каждого потока — они у линий разные."
      />

      <Card>
        <Table
          loading={loading}
          dataSource={lines}
          columns={columns as any}
          rowKey="_id"
          pagination={false}
        />
      </Card>

      <Modal
        title={editing ? "Изменить линию" : "Новая линия переработки"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={860}
        okText="Сохранить"
        cancelText="Отмена"
      >
        {formError && (
          <Alert type="error" showIcon message={formError} style={{ marginBottom: 16 }} />
        )}

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Название линии"
            rules={[{ required: true, message: "Укажите название" }]}
          >
            <Input placeholder="Аппарат переработки Хокистара" />
          </Form.Item>

          <Form.Item name="machineId" label="Станок (необязательно)">
            <Select
              allowClear
              placeholder="Не привязан"
              options={machines.map((m) => ({ value: m._id, label: m.name }))}
            />
          </Form.Item>

          <Form.Item
            name="input"
            label="Позиция на входе"
            rules={[{ required: true, message: "Выберите позицию" }]}
          >
            <Select showSearch optionFilterProp="label" options={options} placeholder="Что перерабатываем" />
          </Form.Item>

          <Title level={5}>Выходы</Title>
          <Form.List name="outputs">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space
                    key={field.key}
                    align="baseline"
                    wrap
                    style={{ display: "flex", marginBottom: 8 }}
                  >
                    <Form.Item
                      name={[field.name, "item"]}
                      rules={[{ required: true, message: "Позиция" }]}
                      style={{ minWidth: 260, marginBottom: 0 }}
                    >
                      <Select showSearch optionFilterProp="label" options={options} placeholder="Позиция" />
                    </Form.Item>

                    <Form.Item
                      name={[field.name, "normPercent"]}
                      rules={[{ required: true, message: "Норма" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={0} max={100} step={0.01} addonAfter="% норма" style={{ width: 170 }} />
                    </Form.Item>

                    <Form.Item
                      name={[field.name, "lowerLimitPercent"]}
                      rules={[{ required: true, message: "Допуск" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={0} max={100} step={0.5} addonAfter="% вниз" style={{ width: 150 }} />
                    </Form.Item>

                    <Form.Item
                      name={[field.name, "upperLimitPercent"]}
                      rules={[{ required: true, message: "Допуск" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={0} max={100} step={0.5} addonAfter="% вверх" style={{ width: 150 }} />
                    </Form.Item>

                    <Form.Item
                      name={[field.name, "isRequired"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Switch checkedChildren="обяз." unCheckedChildren="необяз." />
                    </Form.Item>

                    <Button type="link" danger onClick={() => remove(field.name)}>
                      Убрать
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ lowerLimitPercent: 5, upperLimitPercent: 5, isRequired: true })} block>
                  Добавить выход
                </Button>
              </>
            )}
          </Form.List>

          <Form.Item
            name="loss"
            label="Позиция для учёта потери"
            extra="Отдельная скрытая позиция-счётчик. Приходуется по весу, но с нулевой себестоимостью."
            rules={[{ required: true, message: "Выберите позицию" }]}
            style={{ marginTop: 16 }}
          >
            <Select showSearch optionFilterProp="label" options={options} placeholder="Потеря переработки" />
          </Form.Item>

          <Form.Item
            name="costAllocation"
            label="Разноска себестоимости"
            extra="По стоимости продажи — дорогой поток забирает большую долю. По массе — поровну на килограмм, из-за чего дешёвый мусор станет убыточным."
          >
            <Select
              options={[
                { value: "salesValue", label: "По стоимости продажи (рекомендуется)" },
                { value: "mass", label: "По массе" },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Линия включена"
            valuePropName="checked"
            extra="Включить можно только если у всех товарных выходов проставлены цены — иначе распределять себестоимость не от чего."
          >
            <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
